import { Alert, Button, Card, Form, Modal, Progress, Radio, Select, Space, Typography } from 'antd'
import { useState } from 'react'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { useRebuildableJobsQuery, useRebuildAllTenants, useRebuildSnapshotsMutation } from './rebuildSnapshotsQueries'
import {
  RebuildScope,
  RebuildScopes,
  filterJobOption,
  getAllTenantsAlertType,
  getAllTenantsConfirmTitle,
  getAllTenantsPercent,
  getAllTenantsProgressMessage,
  getConfirmTitle,
  getQueuedMessage,
  getRebuildErrorMessage,
  needsTenantPicker,
  toJobOptions,
  toRebuildRequest,
} from './rebuildSnapshotsModel'

const { Paragraph, Text } = Typography

const RebuildSnapshotsSection = () => {
  const [modal, modalContextHolder] = Modal.useModal()
  const [scope, setScope] = useState<RebuildScope>(RebuildScopes.Tenant)
  const [selectedTenantId, setSelectedTenantId] = useState<number | undefined>(undefined)
  const [jobId, setJobId] = useState<number | undefined>(undefined)
  const tenantsQuery = useSidebarTenantsQuery()
  const jobsQuery = useRebuildableJobsQuery(selectedTenantId, scope === RebuildScopes.Job)
  const mutation = useRebuildSnapshotsMutation()
  const allTenants = useRebuildAllTenants()
  const tenants = tenantsQuery.data ?? []
  const isAllTenantsRunning = allTenants.isRunning

  const tenantName = tenantsQuery.data?.find(tenant => tenant.id === selectedTenantId)?.name ?? ''
  const jobOptions = toJobOptions(jobsQuery.data ?? [])
  const jobLabel = jobOptions.find(option => option.value === jobId)?.label ?? ''
  const request = toRebuildRequest(scope, selectedTenantId, jobId)
  const canRebuild = scope === RebuildScopes.AllTenants
    ? tenants.length > 0 && !isAllTenantsRunning
    : request !== null

  const handleScopeChange = (nextScope: RebuildScope) => {
    setScope(nextScope)
    mutation.reset()
    allTenants.reset()
  }

  const handleRebuildAllTenants = () => {
    modal.confirm({
      title: getAllTenantsConfirmTitle(tenants.length),
      okText: 'Rebuild every tenant',
      cancelText: 'Cancel',
      width: 540,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            This rebuilds every tenant, one after another, queueing a refresh message per job.
            Keep this page open until it finishes.
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            A tenant that fails does not stop the rest. Rebuilding is idempotent and safe to re-run.
          </Paragraph>
        </div>
      ),
      onOk: () => {
        allTenants.start(tenants)
      },
    })
  }

  const handleTenantChange = (tenantId: number) => {
    setSelectedTenantId(tenantId)
    setJobId(undefined)
    mutation.reset()
  }

  const handleJobChange = (nextJobId: number) => {
    setJobId(nextJobId)
    mutation.reset()
  }

  const handleRebuild = () => {
    if (scope === RebuildScopes.AllTenants) {
      handleRebuildAllTenants()
      return
    }
    if (request === null) {
      return
    }
    modal.confirm({
      title: getConfirmTitle(scope, tenantName, jobLabel),
      okText: 'Rebuild',
      cancelText: 'Cancel',
      width: 540,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            This queues a refresh message per job; the snapshot listener recomputes each snapshot
            asynchronously, so results appear as the queue drains.
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Rebuilding is idempotent and safe to re-run.
          </Paragraph>
        </div>
      ),
      onOk: () => {
        mutation.mutate(request)
      },
    })
  }

  return (
    <Card title="Rebuild Job Snapshots" style={{ maxWidth: 760 }}>
      {modalContextHolder}
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Repairs stale JobSnapshots on demand for every tenant, a whole tenant or a single job. The rebuild is
          queued and snapshots recompute asynchronously via the message listener.
        </Paragraph>

        <Radio.Group
          value={scope}
          onChange={event => handleScopeChange(event.target.value)}
          optionType="button"
          disabled={isAllTenantsRunning}
          options={[
            { label: 'All tenants', value: RebuildScopes.AllTenants },
            { label: 'Whole tenant', value: RebuildScopes.Tenant },
            { label: 'Single job', value: RebuildScopes.Job },
          ]}
        />

        {needsTenantPicker(scope) && (
          <Form layout="vertical" style={{ marginBottom: 0 }}>
            <Form.Item label="Tenant" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                placeholder="Search for a tenant"
                value={selectedTenantId}
                onChange={handleTenantChange}
                options={toTenantOptions(tenantsQuery.data ?? [])}
                filterOption={filterTenantOption}
                loading={tenantsQuery.isLoading}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Form>
        )}

        {scope === RebuildScopes.Job && (
          <Form layout="vertical" style={{ marginBottom: 0 }}>
            <Form.Item label="Job" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                placeholder={selectedTenantId === undefined ? 'Select a tenant first' : 'Search for a job'}
                value={jobId}
                onChange={handleJobChange}
                options={jobOptions}
                filterOption={filterJobOption}
                loading={jobsQuery.isLoading}
                disabled={selectedTenantId === undefined}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Form>
        )}

        {scope === RebuildScopes.Job && jobsQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Failed to load the tenant's jobs"
            description="Change the tenant or re-select it to try again."
          />
        )}

        <Button
          type="primary"
          onClick={handleRebuild}
          loading={mutation.isLoading || isAllTenantsRunning}
          disabled={!canRebuild}
        >
          Rebuild snapshots
        </Button>

        {scope === RebuildScopes.AllTenants && allTenants.progress !== null && (
          <Alert
            type={getAllTenantsAlertType(allTenants.progress, isAllTenantsRunning)}
            showIcon
            message={isAllTenantsRunning ? 'Rebuilding all tenants' : 'All tenants rebuilt'}
            description={
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Progress
                  percent={getAllTenantsPercent(allTenants.progress)}
                  size="small"
                  status={isAllTenantsRunning ? 'active' : undefined}
                />
                <Text>{getAllTenantsProgressMessage(allTenants.progress)}</Text>
                {allTenants.progress.failures.length > 0 && (
                  <div>
                    <Text strong>Failed tenants</Text>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {allTenants.progress.failures.map(failure => (
                        <li key={failure.tenantId}>
                          {failure.tenantName} ({failure.tenantId}): {failure.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Space>
            }
          />
        )}

        {mutation.isSuccess && (
          <Alert
            type="success"
            showIcon
            message="Rebuild queued"
            description={getQueuedMessage(mutation.data.jobCount)}
          />
        )}

        {mutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Rebuild failed"
            description={getRebuildErrorMessage(mutation.error)}
          />
        )}
      </Space>
    </Card>
  )
}

export default RebuildSnapshotsSection
